import React, { use, useCallback, useEffect, useState } from 'react'
import withModal from '../../utils/hoc/withModal'
import { Post } from '../../types/influencer/influencerTypes'
import { createDMAutomation, fetchAvailablePosts, updateDMAutomation } from '../../services/api/dmAutomations';
import { POSTAPIError } from '../../types';
import { toast } from 'react-toastify';
import { ERROR } from '../../constants/errors';
import { getSavedForm, removeSavedForm, saveForm } from '../../utils/localStorage';
import { AppDispatch, RootState } from '../../redux-stores/store';
import { useDispatch } from 'react-redux';
import { fetchAvailablePostsThunk, fetchDMAutomations } from '../../redux-stores/slices/dmAutomationsListSlice';
import LinkPostFields from './LinkPostFields';
import { fetchDMAutomation } from '../../redux-stores/slices/dmAutomationSlice';
import { DMAutomation } from '../../types/automations/dmAutomationTypes';
import { SocialPost } from '../../types/collaborations/CollaborationTypes';
import { useSelector } from 'react-redux';


interface AddLinkProps {
    onClose: () => void,
    post: SocialPost | null,
    onError: (error: POSTAPIError) => void,
    dmAutomation?: DMAutomation | null
}

export interface LinkDataType {
    post: string | null,
    active: boolean,
    url: string,
    button_text: string,
    historical: boolean,
    triggers: string[],
}

const AddLink: React.FC<AddLinkProps> = ({ onClose, post, onError, dmAutomation }) => {

    const dispatch: AppDispatch = useDispatch();

    const [postError, setPOSTError] = useState<POSTAPIError | null>(null);
    const selectedSocialAccount = useSelector((state: RootState) => state.dmAutomationsList.selectedSocialAccount);

    const [formErrors, setFormErrors] = useState<any>({});

    const [isProcessing, setProcessing] = useState(false);

    const getDefaultLinkData = useCallback((): LinkDataType => {

        if (dmAutomation) return {
            active: dmAutomation.active,
            button_text: dmAutomation.button_text,
            historical: dmAutomation.historical,
            post: dmAutomation.social_post.uuid ?? null,
            triggers: dmAutomation.triggers,
            url: dmAutomation.url
        };

        const savedForm = getSavedForm(`dm-automation-${post?.uuid}`)

        if (savedForm)
            return { ...savedForm };

        return {
            post: post?.uuid ?? null,
            active: true,
            url: '',
            button_text: '',
            historical: false,
            triggers: []
        }
    }, [post, dmAutomation])

    const [link, setLink] = useState<LinkDataType>(getDefaultLinkData());

    useEffect(() => {
        setLink(getDefaultLinkData());
        setProcessing(false);
        setPOSTError(null);
    }, [post, getDefaultLinkData, dmAutomation])


    const addLink = async () => {
        try {
            setProcessing(true);
            await createDMAutomation(link);
            toast.success("Smartlink created successfully!");
            removeSavedForm(`dm-automation-${post?.uuid}`)
            if(selectedSocialAccount){
                dispatch(fetchDMAutomations({ socialAccountId: selectedSocialAccount?.social_account?.uuid, page: 1, force: true }))
                dispatch(fetchAvailablePostsThunk({ socialAccountId: selectedSocialAccount?.social_account?.uuid, force: true }))
            }
            onClose();
        } catch (error: any) {
            if (error.response?.data) {
                setPOSTError(error.response.data);
            }
            if (error.response?.data?.errors) {
                setFormErrors(error.response.data.errors);
            }
            setProcessing(false);
        }
    }


    const saveFormData = useCallback(async () => {
        await saveForm(`dm-automation-${post?.uuid}`, link);
    }, [post, link])

    const onPostError = useCallback(async () => {
        if (postError && postError.error_code === ERROR.INSUFFICIENT_PERMISSIONS) {
            await saveFormData();
            onError(postError);
        }
    }, [postError, saveFormData, onError])


    useEffect(() => {
        onPostError();
    }, [postError, onPostError])

    const updateLink = async () => {
        try {
            setProcessing(true);
            await updateDMAutomation(post?.uuid!, { button_text: link.button_text, triggers: link.triggers });
            toast.success("Smartlink updated successfully!");
            dispatch(fetchDMAutomation({ postId: post?.uuid!, force: true }));
            if(selectedSocialAccount){
                dispatch(fetchDMAutomations({ socialAccountId: selectedSocialAccount?.social_account?.uuid, page: 1, force: true }))
            }
            onClose();
        } catch (error: any) {
            if (error.response.data) {
                setPOSTError(error.response.data);
            }
        }
    }



    return (
        <div>
            <div className="menu-title p-3 pb-2 mb-0 border-bottom-1">
                <h4 className="fs-18 fw-600 text-dark">{dmAutomation ? (dmAutomation.status === 'active' ? 'Update Link' : 'View Link') : 'Add Link'}</h4>
            </div>
            <div className="p-2 mt-0 pb-4">
                {
                    post ? (
                        <LinkPostFields
                            link={link}
                            setLink={setLink}
                            onSubmit={() => {
                                if (dmAutomation) return updateLink();
                                return addLink();
                            }}
                            onCancel={onClose}
                            disableSubmission={isProcessing}
                            formErrors={formErrors}
                            post={post}
                            editMode={!!dmAutomation}
                            viewOnly={dmAutomation?.status === 'inactive'}
                        />
                    ) : null
                }
            </div>
        </div>
    )
}

export default withModal(AddLink)